/**
 * Fine-grained reads of the workout store. Each hook subscribes to its own
 * slice, so the tab bar's clock, the log view and the rest view re-render only
 * when what they show changes — A2's single context re-rendered every consumer
 * on every field.
 *
 * The derivations are pure functions of the session (`session-model.ts`);
 * these hooks only select and memoise. `store` is a parameter for stories.
 */
import { useMemo } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { workoutStore } from "./instance";
import {
  type ClockPause,
  canRemoveAt,
  type LiveSession,
  liveOf,
  nextOpenExercise,
  type WorkoutSession,
} from "./session-model";
import type { WorkoutStore } from "./workout-store";

/** The whole session. Prefer a narrower hook below; this re-renders on every change. */
export function useWorkoutSession(given?: WorkoutStore): WorkoutSession | null {
  const store = given ?? workoutStore;
  return useStore(store, (state) => state.session);
}

/** A session is running (started, not ended). */
export function useHasActiveWorkout(given?: WorkoutStore): boolean {
  const store = given ?? workoutStore;
  return useStore(
    store,
    (state) => state.session !== null && state.session.ended === null
  );
}

/** Where the person is: phase, exercise, set, and the numbers of the next set. */
export function useWorkoutCursor(given?: WorkoutStore) {
  const store = given ?? workoutStore;
  return useStore(
    store,
    useShallow((state) =>
      state.session === null
        ? null
        : {
            ended: state.session.ended,
            exIndex: state.session.exIndex,
            kg: state.session.kg,
            phase: state.session.phase,
            reps: state.session.reps,
            sessionId: state.session.sessionId,
            setIndex: state.session.setIndex,
          }
    )
  );
}

/** The rest view's slice. The countdown is `restEndsAt - now`, ticked by the view. */
export function useRestView(given?: WorkoutStore) {
  const store = given ?? workoutStore;
  return useStore(
    store,
    useShallow((state) =>
      state.session === null || state.session.phase !== "rest"
        ? null
        : {
            restEndsAt: state.session.restEndsAt,
            restTotal: state.session.restTotal,
          }
    )
  );
}

export interface LiveWorkoutView extends LiveSession {
  /** The cursor's exercise, with its plan item (and so its `itemId`). */
  readonly current: LiveSession["exercises"][number] | undefined;
  readonly exIndex: number;
  /** The next open exercise after the cursor, else the first, else -1. */
  readonly nextOpen: number;
}

/**
 * Today's workout as the session sees it: T22's `liveWorkout` over the plan
 * items, the swaps, the readiness adjustments (the server's once the start
 * synced) and the hand-added sets. Recomputed only when one of those moves.
 */
export function useLiveWorkout(given?: WorkoutStore): LiveWorkoutView | null {
  const store = given ?? workoutStore;
  const inputs = useStore(
    store,
    useShallow((state) =>
      state.session === null
        ? null
        : {
            adjustments: state.session.adjustments,
            exIndex: state.session.exIndex,
            extra: state.session.extra,
            items: state.session.items,
            logged: state.session.logged,
            swaps: state.session.swaps,
          }
    )
  );
  return useMemo(() => {
    if (inputs === null) {
      return null;
    }
    const live = liveOf(inputs);
    return {
      ...live,
      current: live.exercises[inputs.exIndex],
      exIndex: inputs.exIndex,
      nextOpen: nextOpenExercise(inputs, live, inputs.exIndex),
    };
  }, [inputs]);
}

/** Whether "−" may take a pending set off exercise `i` (default: the cursor's). */
export function useCanRemoveSet(i?: number, given?: WorkoutStore): boolean {
  const store = given ?? workoutStore;
  return useStore(store, (state) =>
    state.session === null
      ? false
      : canRemoveAt(state.session, i ?? state.session.exIndex)
  );
}

export interface LiveSessionBadge {
  readonly doneSets: number;
  /** The clock's pauses (`D245`); the clock is `activeElapsedMs`. */
  readonly pauses: readonly ClockPause[];
  readonly phase: WorkoutSession["phase"];
  /** Epoch ms; `null` outside a rest. The shell ticks its own countdown. */
  readonly restEndsAt: number | null;
  /** Epoch ms; the shell's clock is `now - startedAt` less the pauses. */
  readonly startedAt: number;
  readonly totalSets: number;
}

/**
 * T48's live-session button: the clock's origin, the rest, and done/total.
 * `null` when no session is running. Ticking is the shell's, and only while
 * this is non-null.
 */
export function useLiveSessionBadge(
  given?: WorkoutStore
): LiveSessionBadge | null {
  const store = given ?? workoutStore;
  const slice = useStore(
    store,
    useShallow((state) =>
      state.session === null || state.session.ended !== null
        ? null
        : {
            adjustments: state.session.adjustments,
            extra: state.session.extra,
            items: state.session.items,
            logged: state.session.logged,
            pauses: state.session.pauses,
            phase: state.session.phase,
            restEndsAt: state.session.restEndsAt,
            startedAt: state.session.startedAt,
            swaps: state.session.swaps,
          }
    )
  );
  return useMemo(() => {
    if (slice === null) {
      return null;
    }
    const live = liveOf(slice);
    return {
      doneSets: live.doneSets,
      pauses: slice.pauses ?? [],
      phase: slice.phase,
      restEndsAt: slice.restEndsAt,
      startedAt: slice.startedAt,
      totalSets: live.totalSets,
    };
  }, [slice]);
}
